// src/components/ItemGallery.jsx
//
// Main image with thumbnails. Swipe on a phone, arrow keys on a desktop, and
// the thumbnails for either. Only opacity is animated between images, so a
// fast swipe through ten photos never stutters.

import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ChevronLeft, ChevronRight, Package } from "lucide-react";
import { mediaUrl } from "../lib/format";

export default function ItemGallery({ images = [], title }) {
  const [i, setI] = useState(0);
  const n = images.length;

  const go = useCallback((d) => setI((v) => (v + d + n) % n), [n]);

  useEffect(() => {
    if (n < 2) return;
    const onKey = (e) => {
      // Leave the keys alone while someone is typing in the enquiry form
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName)) return;
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, n]);

  if (!n) {
    return (
      <div className="grid aspect-[4/3] place-items-center rounded-2xl border border-line bg-surface2 text-subtle">
        <Package size={40} strokeWidth={1.2} />
      </div>
    );
  }

  const current = images[i];

  return (
    <div>
      <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-line bg-surface2">
        <AnimatePresence initial={false}>
          <motion.img
            key={current.id}
            src={mediaUrl(current.path)}
            alt={`${title} - photo ${i + 1} of ${n}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            drag={n > 1 ? "x" : false}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={(_, info) => {
              if (info.offset.x < -60) go(1);
              if (info.offset.x > 60) go(-1);
            }}
            className="absolute inset-0 h-full w-full cursor-grab object-cover active:cursor-grabbing"
          />
        </AnimatePresence>

        {n > 1 && (
          <>
            <Arrow side="left" onClick={() => go(-1)} />
            <Arrow side="right" onClick={() => go(1)} />
            <span className="absolute bottom-3 right-3 rounded-full bg-bg/85 px-2.5 py-1 font-mono text-[10px] tabular-nums text-muted backdrop-blur">
              {i + 1} / {n}
            </span>
          </>
        )}
      </div>

      {n > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {images.map((img, idx) => (
            <button
              key={img.id}
              type="button"
              onClick={() => setI(idx)}
              aria-label={`Show photo ${idx + 1}`}
              aria-current={idx === i}
              className={`h-16 w-20 shrink-0 overflow-hidden rounded-lg border-2 transition ${
                idx === i ? "border-brooke-red" : "border-transparent opacity-60 hover:opacity-100"
              }`}
            >
              <img
                src={mediaUrl(img.thumb || img.path)}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Arrow({ side, onClick }) {
  const Icon = side === "left" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={side === "left" ? "Previous photo" : "Next photo"}
      className={`absolute top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-line bg-bg/85 text-muted backdrop-blur transition hover:border-brooke-red hover:text-ink ${
        side === "left" ? "left-3" : "right-3"
      }`}
    >
      <Icon size={18} />
    </button>
  );
}
